import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { AuthShell, Field } from "@/components/observer/AuthShell";
import { btnPrimary } from "@/lib/ui";
import { useAuth } from "@/lib/user";

const FOOTER = <>Meeting data stays on this server. Audio is deleted after transcription. <Link to="/privacy" className="underline hover:text-[var(--o-fg)]" data-testid="login-privacy-link">Privacy</Link> · <Link to="/" className="underline hover:text-[var(--o-fg)]" data-testid="login-about-link">About Observer</Link></>;

// People sign in with the email and password their admin set up for them. The admin's own door is /admin/login.
export default function Login() {
  const nav = useNavigate();
  const { signIn } = useAuth();
  const [status, setStatus] = useState(null);
  const [f, setF] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    api.get("/auth/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ has_accounts: true }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email: f.email, password: f.password });
      signIn(data.user);
      nav("/", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      testid="login-page"
      eyebrow="Sign in"
      title={<>A thinking partner<br />in the room.</>}
      intro="It listens, holds the context, and speaks only when asked."
      footer={FOOTER}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email" type="email" value={f.email} onChange={set("email")} autoComplete="username" autoFocus data-testid="login-email-input" />
        <Field label="Password" type="password" value={f.password} onChange={set("password")} autoComplete="current-password" data-testid="login-password-input" />
        {error && <p className="text-sm text-[#EF4444] font-mono" data-testid="login-error">{error}</p>}
        <button type="submit" disabled={busy || !f.email || !f.password} className={`${btnPrimary} w-full py-3.5`} data-testid="login-submit-button">
          {busy ? <CircleNotch size={16} className="animate-spin" /> : null} Sign in <ArrowRight size={16} weight="bold" />
        </button>
        {status && !status.has_accounts && (
          <p className="text-xs text-[var(--o-muted)] border-l border-[var(--o-border-strong)] pl-3" data-testid="login-no-accounts">
            Nobody has an account on this install yet. The admin adds people from the Admin area and sends each a setup link.
          </p>
        )}
        <p className="text-xs text-[var(--o-muted3)]">
          Forgot your password? Ask your admin for a reset link. · <Link to="/admin/login" className="underline hover:text-[var(--o-fg)]" data-testid="login-admin-link">Admin sign-in</Link>
        </p>
      </form>
    </AuthShell>
  );
}
